import React, { useCallback, useRef, useState } from 'react';

// The drag/drop + file-picker mechanics, shared by the photo dropzone and the
// footage-video dropzone. Presentational only — the parent decides what to do
// with the files (usually: kick off preflight).
//
// Extracted from PhotoDropzone when multi-video upload landed: the two zones
// differ in copy, icon and `accept`, and in nothing else. Keeping one copy of
// the drag-state/keyboard/hidden-input handling is what stops them drifting.
//
// Two shapes: the default banner used to add more files to a project that
// already has some, and `variant="tile"`, the peer tiles on a bare project.
//
// Dropping files still works and is still wired, but no copy anywhere PROMISES
// it: the click-to-pick path is the one we stand behind.

const FileDropzone = ({
  onFiles,
  accept,
  multiple = true,
  icon,
  title,
  subtitle,
  ctaLabel,
  disabled,
  variant,
  testId,
  inputTestId,
}) => {
  const inputRef = useRef(null);
  const [isDragOver, setDragOver] = useState(false);

  const handleFiles = useCallback((fileList) => {
    if (!fileList || !onFiles) return;
    const arr = Array.from(fileList);
    if (arr.length > 0) onFiles(arr);
  }, [onFiles]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const pickFiles = () => { if (!disabled) inputRef.current?.click(); };

  // Same hidden input in both shapes — the picker, its accept filter and its
  // multiple flag are the contract the upload pipeline is built on.
  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      {...(multiple ? { multiple: true } : {})}
      onChange={(e) => handleFiles(e.target.files)}
      style={{ display: 'none' }}
      data-testid={inputTestId}
      disabled={disabled}
    />
  );

  // The tile variant has no disabled state: its callers render it on a bare,
  // idle project and unmount the moment a scan starts. `disabled` still
  // applies to the default variant below.
  if (variant === 'tile') {
    return (
      <div
        className="pi-footage-tile"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        data-testid={testId}
      >
        {icon}
        <b className="pi-footage-tile-title">{title}</b>
        <p className="pi-footage-tile-hint">{subtitle}</p>
        <button
          type="button"
          className="btn btn-teal pi-footage-tile-cta"
          onClick={pickFiles}
        >
          {ctaLabel}
        </button>
        {fileInput}
      </div>
    );
  }

  return (
    <div
      className={`pi-dropzone${isDragOver ? ' pi-dropzone--over' : ''}${disabled ? ' pi-dropzone--disabled' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      role="button"
      tabIndex={0}
      onClick={pickFiles}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
      }}
      data-testid={testId}
    >
      <b>{title}</b>
      <p className="pi-dropzone-hint">{subtitle}</p>
      {fileInput}
    </div>
  );
};

export default FileDropzone;
